import React, { createContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import useSearchAPIRequest from "../hooks/use-search-api-request";
import {
  addFilterTransformation,
  setFilterTransformation,
  removeFilterTransformation,
  resetFilterTransformation,
} from "../lib/transformations";
import parseUrlState from "../url-sync/parse-url-state";

// Create the solr context.
export const SolrContext = createContext({});

// The SolrProvider makes the context store accessible to children.
const SolrProvider = ({ children, config, initialFilters, initialSorting, initialSearchQuery }) => {
  const defaultConfig = config;

  const [searchQueryString, setSearchQueryString] = useState(initialSearchQuery || "");
  const [currentFilters, setCurrentFilters] = useState(initialFilters || null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(defaultConfig.limit || null);
  const [facetSelector, setFacetSelector] = useState("");
  const [currentSorting, setCurrentSorting] = useState(initialSorting || "");

  /**
   * Re-sync filters/sorting/query from the URL whenever the location changes
   * (e.g. navigating between two links that only differ in query string).
   * The initial values above already cover the first render.
   */
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const { filters, sorting, searchQuery } = parseUrlState();
    setCurrentFilters(filters);
    setCurrentSorting(sorting || "");
    setSearchQueryString(searchQuery || "");
  }, [location.search]);

  /**
   * Merge the default config with the current filters and sorting.
   * This config is put into the useSearchAPIRequest hook to perform the request.
   */
  const _config = {
    ...defaultConfig,
    filters: currentFilters,
    sort: currentSorting,
    query: searchQueryString,
    limit: currentLimit,
  };

  /**
   * Fetch data from the API using the query string.
   */
  const { data, loading, status, error, fetchMore } = useSearchAPIRequest(_config);

  // Extract nodes from the result.
  const items = data?.search_results || [];

  // Extract metadata from the result.
  const metaData = {
    firstPageLoaded: !!data && typeof data.search_results_page === "number",
    currentPage: data?.search_results_page || 0,
    totalRows: data?.search_results_count || 0,
    perPage: data?.search_results_per_page || 10,
    pageCount: data?.search_results_pages || 0,
  };

  /**
   * Add a value to a filter.
   * @param {string} filterId The filter id (set in config)
   * @param {*} filterValue The value to be added
   */
  const addFilter = (filterId, filterValue) => {
    setCurrentFilters((prevFilters) =>
      addFilterTransformation(prevFilters, filterId, filterValue)
    );
  };

  /**
   * Set the value of a filter.
   * @param {string} filterId The filter id (set in config)
   * @param {*} filterValues The value to be set
   */
  const setFilter = (filterId, filterValues) => {
    setCurrentFilters((prevFilters) =>
      setFilterTransformation(prevFilters, filterId, filterValues)
    );
  };

  /**
   * Remove a value of a filter.
   * @param {string} filterId The filter id (set in config)
   * @param {*} filterValue The value to be removed
   */
  const removeFilter = (filterId, filterValue) => {
    setCurrentFilters((prevFilters) =>
      removeFilterTransformation(prevFilters, filterId, filterValue)
    );
  };

  /**
   * Reset a single filter.
   * @param {string} filterId Filter id (set in config)
   */
  const resetFilter = (filterId) => {
    setCurrentFilters((prevFilters) =>
      resetFilterTransformation(prevFilters, filterId)
    );
  };

  /**
   * Reset all filters.
   */
  const resetFilters = () => {
    setCurrentFilters(null);
  };

  /**
   * Check, if a filter exists or a value is set.
   * @param {string} filterId The filter id (set in config)
   * @param {*} filterValue The optional value
   * @returns {boolean}
   */
  const isFilterSet = (filterId, filterValue) => {
    const filterIsSet =
      currentFilters &&
      typeof currentFilters[filterId] !== "undefined" &&
      currentFilters[filterId].length > 0;
    if (typeof filterValue === "undefined") {
      return filterIsSet;
    }
    return filterIsSet && currentFilters[filterId].includes(filterValue);
  };

  /**
   * Sort the results.
   * @param {string} sortField 
   * @param {"asc"|"desc"} sortOrder
   */
  const setSorting = (sortField, sortOrder) => {
    setCurrentSorting(`${sortField}:${sortOrder}`);
  };

  const resetSorting = () => {
    setCurrentSorting("");
  };

  // The context value provides all necessary values and functions.
  const value = {
    config: _config,
    searchQueryString,
    setSearchQueryString,
    metaData,
    currentPage,
    setCurrentPage,
    setCurrentLimit,
    fetchMore,
    currentFilters,
    addFilter,
    setFilter,
    removeFilter,
    resetFilter,
    resetFilters,
    isFilterSet,
    items,
    loading,
    status,
    error,
    facets: data?.facets || [],
    facetSelector,
    setFacetSelector,
    currentSorting,
    setSorting,
    resetSorting,
  };

  return <SolrContext.Provider value={value}>{children}</SolrContext.Provider>;
};

SolrProvider.propTypes = {
  children: PropTypes.node.isRequired,
  config: PropTypes.object.isRequired,
  initialFilters: PropTypes.object,
  initialSorting: PropTypes.string,
  initialSearchQuery: PropTypes.string,
};

export default SolrProvider;
